import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Separator } from '@/components/ui/separator';
import SEO from '@/components/SEO';
import { useEffect, useState } from 'react';
import { blogPosts } from '@/data/blogPosts';
import { motion } from 'framer-motion';
import { BookOpen, Calendar, Clock, MessageSquare, Share, Tag, Lightbulb, ArrowRight, FileText, Rocket, Settings, CheckCircle, BarChart, Zap, Target, DollarSign, Shield, ArrowLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LoadingAnimation } from '@/components/LoadingAnimation';
import { validateBlogSlug } from '@/utils/security';

const BlogPostDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const validatedSlug = validateBlogSlug(slug);
  const post = validatedSlug ? blogPosts.find(post => post.slug === validatedSlug) : null;
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [slug]);

  // Generate table of contents from post content
  const generateTableOfContents = () => {
    if (!post) return [];
    
    const toc = [];
    
    // Add introduction
    toc.push({ id: 'introduction', title: 'Introduction', level: 1 });
    
    post.content.forEach((section, index) => {
      if (section.type === 'heading') {
        toc.push({
          id: `section-${index}`,
          title: section.content,
          level: 1
        });
      } else if (section.type === 'subheading') {
        toc.push({
          id: `subsection-${index}`,
          title: section.content,
          level: 2
        });
      }
    });
    
    return toc;
  };

  // Scroll to section
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 100;
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = generateTableOfContents();
      const scrollPosition = window.scrollY + 150;

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post]);
  
  if (!post) {
    return <PageLayout showFooter={false}>
        <SEO title="Post Not Found - CIPHER Security Technologies" description="The requested blog post could not be found." />
        <section className="min-h-screen flex items-center justify-center" style={{ background: "#0f0f17" }}>
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4 text-white">Post not found</h1>
            <p className="text-gray-400 text-lg mb-6">We couldn't find the post you're looking for.</p>
            <Link
              to="/blog"
              className="inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-all hover:shadow-lg"
              style={{
                background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                color: "#fff"
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Link>
          </div>
        </section>
      </PageLayout>;
  }

  // Calculate reading time
  const wordCount = post.content.reduce((count, section) => {
    if (section.content) {
      return count + section.content.split(/\s+/).length;
    } else if (section.items) {
      return count + section.items.join(' ').split(/\s+/).length;
    }
    return count;
  }, 0);
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  const formatDateForISO = (dateStr: string) => {
    if (!dateStr) return '';
    const months: Record<string, string> = {
      'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06',
      'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
    };
    const parts = dateStr.split(' ');
    if (parts.length === 3) {
      const month = months[parts[0]];
      const day = parts[1].replace(',', '');
      const year = parts[2];
      return `${year}-${month}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  };

  const tableOfContents = generateTableOfContents();
  const isProcessPost = validatedSlug === 'from-idea-to-launch-development-process';
  const isSensorPost = validatedSlug === 'leveraging-sensor-technology-product-development';

  const extractKeywords = () => {
    const keywords = ['smart textiles', 'product development', post.category.toLowerCase()];
    if (post.title.includes('Development Process')) {
      keywords.push('development process', 'manufacturing', 'prototyping', 'smart product design');
    }
    return keywords;
  };
  
  return <PageLayout showFooter={false}>
      <SEO
        title={`${post.title} - CIPHER Security Technologies`}
        description={post.excerpt}
        imageUrl={post.imageUrl}
        type="article"
        isBlogPost={true}
        publishDate={formatDateForISO(post.date)}
        modifiedDate={formatDateForISO(post.date)}
        author={post.author}
        category={post.category}
        keywords={extractKeywords()}
      />
      
      <div className="min-h-screen" style={{ background: "#0f0f17" }}>
        <div className="flex pt-32">{/* Increased from pt-24 to pt-32 to ensure content is below navbar */}
          {/* Fixed Left Sidebar - Table of Contents */}
          {!isMobile && (
            <div className="w-80 fixed left-0 top-16 h-screen overflow-y-auto border-r" style={{ 
              background: "#0f0f17", 
              borderColor: "rgba(6, 182, 212, 0.1)" 
            }}>
              <div className="p-6">
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-6" style={{ color: "#9ca3af" }}>
                  In This Article
                </h3>
                
                {/* Article metadata */}
                <div className="mb-8 p-4 rounded-lg border" style={{ 
                  background: "rgba(15, 15, 23, 0.8)", 
                  borderColor: "rgba(6, 182, 212, 0.1)" 
                }}>
                  <h2 className="font-semibold text-white text-lg mb-3 leading-tight">{post.title}</h2>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-400">
                      <BookOpen size={14} className="mr-2" />
                      By {post.author}
                    </div>
                    <div className="flex items-center text-gray-400">
                      <Calendar size={14} className="mr-2" />
                      {post.date}
                    </div>
                    <div className="flex items-center text-gray-400">
                      <Clock size={14} className="mr-2" />
                      {readingTime} min read
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="inline-block px-3 py-1 text-xs font-medium rounded-full" style={{
                      background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                      color: "#fff"
                    }}>
                      {post.category}
                    </span>
                  </div>
                </div>

                {/* Table of Contents */}
                <nav className="space-y-1">
                  {tableOfContents.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={cn(
                        "w-full text-left py-2 px-3 text-sm rounded-md transition-all duration-200",
                        item.level === 2 ? "ml-4 text-xs" : "",
                        activeSection === item.id 
                          ? "bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 text-white border-l-2 pl-2" 
                          : "text-gray-400 hover:text-white hover:bg-gray-800/50"
                      )}
                      style={activeSection === item.id ? { borderLeftColor: "#00a86b" } : {}}
                    >
                      <span className="flex items-center">
                        {activeSection === item.id && <ChevronRight size={14} className="mr-1 flex-shrink-0" />}
                        <span className="truncate">{item.title}</span>
                      </span>
                    </button>
                  ))}
                </nav>

                {/* Call to Action Removed */}
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className={cn("flex-1", !isMobile ? "ml-80" : "")}>
            <div className="max-w-4xl mx-auto px-6 py-8">
              {isLoading ? (
                <div className="flex justify-center py-20">
                  <LoadingAnimation />
                </div>
              ) : (
                <>
                  {/* Back to Blog Button */}
                  <div className="mb-8">
                    <Link
                      to="/blog"
                      className="inline-flex items-center text-sm font-medium transition-colors hover:text-cyan-400"
                      style={{ color: "#d1d5db" }}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Blog
                    </Link>
                  </div>
                  {/* Article Header */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-12"
                    id="introduction"
                  >
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{ color: "#fff" }}>
                      {post.title}
                    </h1>
                    <p className="text-xl leading-relaxed" style={{ color: "#9ca3af" }}>
                      {post.excerpt}
                    </p>
                  </motion.div>

                  {/* Article Content */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="prose prose-lg max-w-none"
                  >
                    {/* Content rendering based on post type */}
                    {isProcessPost ? (
                      <div className="space-y-12">
                        {/* Process post specific content */}
                        <div className="mb-8">
                          <p className="mb-6 text-lg leading-relaxed" style={{ color: "#e5e7eb" }}>
                            At CIPHER Security, we simplify the journey from vulnerability identification to comprehensive security implementation. Whether you're starting with basic security assessments or already have security frameworks ready to enhance, we're here to support you exactly where you need us.
                          </p>
                        </div>

                        {/* Process Steps */}
                        {[
                          { 
                            icon: Lightbulb, 
                            title: "Security Assessment & Discovery", 
                            content: "Comprehensive security conversation to understand your current infrastructure and security posture.",
                            color: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)"
                          },
                          { 
                            icon: FileText, 
                            title: "Vulnerability Assessment & Testing", 
                            content: "Comprehensive offensive security testing and vulnerability analysis using advanced SAST, DAST, and manual testing techniques.",
                            color: "linear-gradient(135deg, #06b6d4 0%, #a78bfa 100%)"
                          },
                          { 
                            icon: CheckCircle, 
                            title: "Remediation & Security Enhancement", 
                            content: "Work with your team to implement comprehensive security improvements and proper security controls.",
                            color: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)"
                          },
                          { 
                            icon: Settings, 
                            title: "DevSecOps", 
                            content: "Integrate security into your development lifecycle with automated testing and continuous monitoring.",
                            color: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                          },
                          { 
                            icon: Rocket, 
                            title: "Ongoing Security Monitoring", 
                            content: "Establish continuous security monitoring with ongoing threat detection and proactive vulnerability management.",
                            color: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                          }
                        ].map((step, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.1 * index }}
                            id={`section-${index}`}
                            className="mb-12"
                          >
                            <div className="flex items-center gap-4 mb-6">
                              <div
                                className="w-12 h-12 rounded-full flex items-center justify-center"
                                style={{ background: step.color }}
                              >
                                <step.icon size={24} className="text-white" />
                              </div>
                              <h2 className="text-2xl font-bold text-white">
                                Step {index + 1}: {step.title}
                              </h2>
                            </div>
                            <div className="pl-16">
                              <p className="text-lg leading-relaxed" style={{ color: "#e5e7eb" }}>
                                {step.content}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      /* Standard content rendering */
                      <div className="space-y-8">
                        {post.content.map((section, index) => (
                          <motion.div 
                            key={index} 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.1 * index }}
                            id={section.type === 'heading' ? `section-${index}` : section.type === 'subheading' ? `subsection-${index}` : undefined}
                            className="mb-8"
                          >
                            {section.type === 'paragraph' && (
                              <p className="text-lg leading-relaxed" style={{ color: "#e5e7eb" }}>
                                {section.content}
                              </p>
                            )}
                            
                            {section.type === 'heading' && (
                              <h2 className="text-3xl font-bold mt-12 mb-6 text-white border-b pb-4" style={{ borderColor: "rgba(6, 182, 212, 0.2)" }}>
                                {section.content}
                              </h2>
                            )}
                            
                            {section.type === 'subheading' && (
                              <h3 className="text-xl font-semibold mt-8 mb-4 text-white">
                                {section.content}
                              </h3>
                            )}
                            
                            {section.type === 'list' && (
                              <ul className="list-disc pl-6 space-y-2">
                                {section.items?.map((item, itemIndex) => (
                                  <li key={itemIndex} className="text-lg" style={{ color: "#e5e7eb" }}>
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            )}
                            
                            {section.type === 'quote' && (
                              <blockquote className="border-l-4 pl-6 py-4 my-8 rounded-r-lg italic bg-gradient-to-r from-emerald-500/10 to-transparent" style={{ borderColor: "#00a86b" }}>
                                <p className="text-lg text-gray-300 m-0">{section.content}</p>
                              </blockquote>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    )}

                    {/* Article Footer */}
                    <div className="mt-16 pt-8 border-t" style={{ borderColor: "rgba(6, 182, 212, 0.2)" }}>
                      <div className="flex flex-col sm:flex-row items-center justify-between p-6 rounded-lg" style={{
                        background: "rgba(15, 15, 23, 0.8)",
                        border: "1px solid rgba(6, 182, 212, 0.2)"
                      }}>
                        <div>
                          <p className="text-sm font-medium text-gray-400">
                            Category: <span className="text-emerald-400">{post.category}</span>
                          </p>
                        </div>
                        <div className="mt-4 sm:mt-0">
                          <Link to="/blog">
                            <button className="px-4 py-2 text-sm font-medium rounded-md transition-colors bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white">
                              View All Articles
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>;
};

// Remove the Footer component from BlogPostDetail by not rendering it here.

export default BlogPostDetail;